import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CellTower as TowerIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Videocam as VideocamIcon,
  Sensors as SensorsIcon,
  Public as PublicIcon,
  ElectricBolt as PowerIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const IFTComponentDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/ift-rvss-system');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to IFT/RVSS System"
        >
          Back to IFT/RVSS
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Integrated Fixed Tower (IFT)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Permanent Border Surveillance Tower Infrastructure
          </Typography>
        </Box>
      </Box>

      {/* Component Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Component Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Integrated Fixed Tower (IFT) is a permanent surveillance infrastructure deployed along the U.S. southern 
            border, providing 24/7 persistent monitoring through advanced sensor integration. Standing 80-180 feet tall, 
            each tower is equipped with day/night cameras, thermal imaging, ground radar, and communication systems, 
            offering comprehensive situational awareness across 15-20 square miles per tower. IFT represents a force 
            multiplier for Border Patrol agents, enabling detection and tracking of illegal border crossings in remote 
            and challenging terrain.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Component Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="IFT Component 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is IFT"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>permanent surveillance tower infrastructure</strong> consisting of <strong>80-180 foot tall 
                    towers</strong> equipped with <strong>integrated sensor suites including day/night cameras, thermal imaging, 
                    ground radar, and communication systems</strong>. Each tower provides <strong>360-degree coverage</strong> 
                    monitoring <strong>15-20 square miles</strong> with <strong>autonomous operation and remote control 
                    capabilities</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses IFT"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Operated by <strong>U.S. Border Patrol</strong> with monitoring from <strong>sector operations centers</strong>. 
                    Primary users include <strong>remote video surveillance operators, Border Patrol agents in the field, sector 
                    command staff, and tactical coordination teams</strong>. Maintained by <strong>CBP Office of Technology 
                    Innovation and Acquisition (OTIA)</strong> and <strong>contractor technical support teams</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was IFT deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Initial deployment began in <strong>2014-2015</strong> as part of border security modernization. Major 
                    expansion in <strong>2017-2019</strong> with additional tower construction along Arizona border. Technology 
                    upgrades in <strong>2020-2022</strong> with enhanced sensors and AI analytics. Operates <strong>24/7/365</strong> 
                    with <strong>continuous monitoring and automated alert generation</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is IFT deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Deployed along the <strong>U.S.-Mexico border</strong> with primary concentration in <strong>Arizona 
                    (Tucson and Yuma sectors)</strong> and <strong>Texas (Rio Grande Valley)</strong>. Towers positioned in 
                    <strong> remote, high-traffic smuggling corridors</strong> and <strong>areas with limited agent presence</strong>. 
                    Monitored from <strong>sector operations centers</strong> in Tucson, Yuma, El Paso, and McAllen.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why IFT exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To provide <strong>persistent surveillance in remote border areas</strong> where agent presence is limited 
                    or terrain is challenging. Serves as a <strong>force multiplier</strong> enabling detection and tracking of 
                    illegal crossings, drug smuggling, and human trafficking. Critical for <strong>situational awareness, rapid 
                    response coordination, and resource optimization</strong>. Reduces <strong>agent exposure to dangerous 
                    conditions</strong> while maintaining <strong>continuous border monitoring</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Tower Components */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <TowerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Tower Components & Sensors
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VideocamIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Camera Systems
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Advanced optical and thermal imaging for day/night surveillance.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Day Cameras:</strong> High-resolution PTZ (Pan-Tilt-Zoom) with 30x optical zoom</Typography></li>
                <li><Typography variant="body2"><strong>Thermal Cameras:</strong> Long-wave infrared (LWIR) for night detection</Typography></li>
                <li><Typography variant="body2"><strong>360° Coverage:</strong> Multiple cameras for complete perimeter monitoring</Typography></li>
                <li><Typography variant="body2"><strong>Auto-Tracking:</strong> AI-powered target following and classification</Typography></li>
                <li><Typography variant="body2"><strong>Image Stabilization:</strong> Compensates for wind and tower movement</Typography></li>
                <li><Typography variant="body2"><strong>Range:</strong> Visual detection up to 5 miles, identification up to 2 miles</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SensorsIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Radar Systems
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Ground surveillance radar for all-weather detection.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Detection Range:</strong> Movement detection up to 7.5 miles</Typography></li>
                <li><Typography variant="body2"><strong>All-Weather:</strong> Operates in fog, rain, dust, and darkness</Typography></li>
                <li><Typography variant="body2"><strong>Target Classification:</strong> Distinguishes humans, vehicles, animals</Typography></li>
                <li><Typography variant="body2"><strong>Multiple Target Tracking:</strong> Simultaneous tracking of 100+ objects</Typography></li>
                <li><Typography variant="body2"><strong>Low Probability of Intercept:</strong> Difficult to detect by smugglers</Typography></li>
                <li><Typography variant="body2"><strong>Automatic Cueing:</strong> Directs cameras to detected targets</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Communication Systems
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Secure data transmission and remote control capabilities.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Microwave Links:</strong> High-bandwidth data transmission to operations centers</Typography></li>
                <li><Typography variant="body2"><strong>Satellite Backup:</strong> Redundant communication path</Typography></li>
                <li><Typography variant="body2"><strong>Encrypted Channels:</strong> Secure video and data feeds</Typography></li>
                <li><Typography variant="body2"><strong>Remote Control:</strong> Operator control of cameras and sensors</Typography></li>
                <li><Typography variant="body2"><strong>Real-Time Streaming:</strong> Live video to multiple monitoring stations</Typography></li>
                <li><Typography variant="body2"><strong>Alert Distribution:</strong> Automated notifications to field agents</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PowerIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Power & Infrastructure
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Reliable power systems for continuous operation.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Solar Panels:</strong> Primary power generation with battery storage</Typography></li>
                <li><Typography variant="body2"><strong>Grid Connection:</strong> Commercial power where available</Typography></li>
                <li><Typography variant="body2"><strong>Battery Backup:</strong> 72-hour autonomous operation capability</Typography></li>
                <li><Typography variant="body2"><strong>Diesel Generators:</strong> Emergency backup power systems</Typography></li>
                <li><Typography variant="body2"><strong>Environmental Control:</strong> Climate control for electronics</Typography></li>
                <li><Typography variant="body2"><strong>Lightning Protection:</strong> Surge suppression and grounding</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tower Specifications */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <TowerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Tower Specifications
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Specification</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Value</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Tower Height</strong></TableCell>
              <TableCell>80-180 feet</TableCell>
              <TableCell>Varies by terrain and coverage requirements</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Coverage Area</strong></TableCell>
              <TableCell>15-20 sq miles</TableCell>
              <TableCell>Per tower, depending on terrain</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Detection Range</strong></TableCell>
              <TableCell>Up to 7.5 miles</TableCell>
              <TableCell>Radar detection of human-sized targets</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Visual Range</strong></TableCell>
              <TableCell>Up to 5 miles</TableCell>
              <TableCell>Day camera detection capability</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Thermal Range</strong></TableCell>
              <TableCell>Up to 3 miles</TableCell>
              <TableCell>Night/thermal imaging detection</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Power Capacity</strong></TableCell>
              <TableCell>72-hour backup</TableCell>
              <TableCell>Battery storage for autonomous operation</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Communication</strong></TableCell>
              <TableCell>Microwave/Satellite</TableCell>
              <TableCell>Redundant data transmission paths</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Availability</strong></TableCell>
              <TableCell>99.5%+</TableCell>
              <TableCell>System uptime target</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Deployment Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Deployment Scale & Coverage
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                50+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Towers Deployed
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Along southern border
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                750+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Square Miles
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Total coverage area
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Operations
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Continuous monitoring
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                99.5%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Uptime
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                System availability
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Operational Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Operational Impact
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Force Multiplier:</strong> Extends agent coverage in remote areas</Typography></li>
                <li><Typography variant="body2"><strong>Persistent Surveillance:</strong> 24/7 monitoring without fatigue</Typography></li>
                <li><Typography variant="body2"><strong>Rapid Detection:</strong> Immediate alerts for border crossings</Typography></li>
                <li><Typography variant="body2"><strong>Agent Safety:</strong> Reduces exposure to dangerous conditions</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Resource Optimization:</strong> Directs agents to active threats</Typography></li>
                <li><Typography variant="body2"><strong>Evidence Collection:</strong> Video documentation for prosecutions</Typography></li>
                <li><Typography variant="body2"><strong>Situational Awareness:</strong> Real-time border activity intelligence</Typography></li>
                <li><Typography variant="body2"><strong>Cost Effective:</strong> Lower operational cost than manned patrols</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration Points */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Integrations
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="Sector Operations Centers" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Command and control</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="RVSS Network" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Video surveillance system</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Mobile Surveillance" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Tactical units coordination</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Agent Communications" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Field agent radios</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Ground Sensors" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Seismic/acoustic detection</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="GIS Mapping" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Geographic information systems</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="CBP Databases" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Intelligence and records</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Weather Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Environmental monitoring</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default IFTComponentDetail;
